import { Marked } from 'marked';
// DOMPurify directly, not config's sanitizeHtml: config imports the app's stores, and the Help app
// (src/help) renders with this module without them.
import DOMPurify from 'dompurify';

// An instance of its own, so these options never leak into another caller of marked.
const markdown = new Marked({ gfm: true, breaks: true });
// Documents (help articles) are hard-wrapped in their source; there a single newline is a space.
const documentMarkdown = new Marked({ gfm: true, breaks: false });

const escapeHtml = (text) => text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// Lens Help replies name places on screen (a page, menu, tab, dialog, section or field) as
// [[Edit contact]]: an informational chip, so places read differently from buttons, which stay bold.
// Replies don't show breadcrumbs: a path that slips through ([[Contacts › Client]]) shows only its
// last, most specific part. Only replies rendered with { uiPlaces: true } get this syntax.
const uiPlace = {
    name: 'uiPlace',
    level: 'inline',
    start: (src) => {
        const index = src.indexOf('[[');
        return index < 0 ? undefined : index;
    },
    tokenizer(src) {
        const match = /^\[\[([^[\]\n]{1,160})\]\]/.exec(src);
        if (match) return { type: 'uiPlace', raw: match[0], text: match[1].trim() };
        return undefined;
    },
    renderer: (token) => {
        const place = token.text
            .split('›')
            .map((part) => part.trim())
            .filter(Boolean)
            .at(-1);
        return place ? `<span class="bn-ui-place">${escapeHtml(place)}</span>` : '';
    },
};
const uiMarkdown = new Marked({ gfm: true, breaks: true, extensions: [uiPlace] });

/** A name as compared for styling; mirrors KnowledgeSearch::labelKey on the server. */
export const labelKey = (label) =>
    String(label)
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/^[\s:.,;!?"'()]+|[\s:.,;!?"'()]+$/g, '');

/**
 * Styles the on-screen names in a Lens Help reply by what the app says they are (labelKinds, from the
 * reply's result): a button keeps its bold and looks like a button; a place becomes a place chip;
 * any other bold name becomes plain text.
 */
function applyLabelKinds(root, labelKinds) {
    root.querySelectorAll('strong, .bn-ui-place').forEach((el) => {
        if (el.closest('.md-lead-in')) return;
        const kind = labelKinds[labelKey(el.textContent)];
        if (kind === 'button') {
            const button = document.createElement('strong');
            button.className = 'bn-ui-button';
            button.textContent = el.textContent;
            el.replaceWith(button);
        } else if (kind === 'place' && el.nodeName === 'STRONG') {
            const place = document.createElement('span');
            place.className = 'bn-ui-place';
            place.textContent = el.textContent;
            el.replaceWith(place);
        } else if (kind !== 'place') {
            // Not a button or a place the app knows (a placeholder, an option, a word like "as"):
            // plain text, so only real controls and places stand out.
            el.replaceWith(document.createTextNode(el.textContent));
        }
    });
}

/**
 * Markdown (an AI reply, for example) as sanitized HTML, safe for v-html. Links open in a new tab;
 * with `{ links: false }` every link is replaced by its text, so nothing in it is clickable (#6453).
 *
 * DOMPurify drops `target` by default, so the link attributes are added to the already sanitized
 * markup rather than asked of the sanitizer: nothing the markdown says survives unsanitized.
 *
 * With `{ uiPlaces: true }`, [[Page › Tab]] renders as place chips (Lens Help replies); with
 * `{ labelKinds }` (labelKey → 'button' | 'place'), bold names are styled by what they are.
 *
 * @param {string|null|undefined} text
 * @param {{ links?: boolean, breaks?: boolean, uiPlaces?: boolean, labelKinds?: Record<string, string> }} [options]
 * @returns {string}
 */
export function renderMarkdown(text, { links, breaks = true, uiPlaces = false, labelKinds = null } = {}) {
    if (!text) return '';

    const parser = uiPlaces ? uiMarkdown : breaks ? markdown : documentMarkdown;
    const clean = DOMPurify.sanitize(parser.parse(String(text), { async: false }));
    if (!clean) return '';

    const template = document.createElement('template');
    template.innerHTML = clean;
    // A paragraph that is nothing but bold text ("**To record a dose:**") is a lead-in heading, not
    // an on-screen label: mark it so styles can tell the two apart.
    template.content.querySelectorAll('p').forEach((paragraph) => {
        const meaningful = [...paragraph.childNodes].filter((node) => !(node.nodeType === Node.TEXT_NODE && !node.textContent.trim()));
        if (meaningful.length === 1 && meaningful[0].nodeName === 'STRONG') paragraph.classList.add('md-lead-in');
    });
    if (labelKinds && typeof labelKinds === 'object') applyLabelKinds(template.content, labelKinds);
    if (links === false) {
        template.content.querySelectorAll('a').forEach((link) => link.replaceWith(...link.childNodes));
        return template.innerHTML;
    }
    template.content.querySelectorAll('a[href]').forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
    });
    return template.innerHTML;
}
